/**
 * CustomerAutofillPicker — P0 SPRINT (Aug 2026)
 *
 * CANONICAL CUSTOMER AUTOFILL PICKER.
 *
 * Één component voor het hele platform. Vervangt lokale customer-picker
 * varianten in Shipment, Warehouse, Telefoonregistratie, AddPieceDialog,
 * Pickup, Delivery, Offertes, Facturen.
 *
 * Werking:
 *   - Wrapper rond ``EnterpriseEntityPicker`` (mode="inline")
 *   - Zoekt exclusief in `entity_type=customer` — canonical DB source
 *   - metadata bevat de VOLLEDIGE autofill-payload (backend adapter);
 *     als extra velden nodig zijn (bv preferred_language voor edge-case
 *     modules) kan `fetch_full` optioneel aanvullend `/autofill` roepen
 *   - `onSelect(customer)` levert een genormaliseerde customer-payload
 *     (via `normalizeCustomer`). Modules die een sender/recipient nodig
 *     hebben roepen `customerToParty(customer)` aan.
 *
 * Props:
 *   - label              — label boven de picker (default: "Bestaande klant zoeken")
 *   - placeholder        — placeholder in het input
 *   - onSelect(customer) — callback met canonical customer payload
 *   - fetchFull          — indien true, roep ook /autofill aan voor
 *                          gegarandeerde volledigheid (default false)
 *   - autoFocus, showRecent, testIdPrefix — doorgezet naar picker
 */
import React, { useState } from "react";
import { Label } from "../ui/label";
import EnterpriseEntityPicker from "../admin/shared/EnterpriseEntityPicker";
import {
  normalizeCustomer, fetchCustomerAutofill,
} from "../../services/customerAutofillService";

export default function CustomerAutofillPicker({
  label = "Bestaande klant zoeken",
  placeholder,
  onSelect,
  fetchFull = false,
  autoFocus = false,
  showRecent = true,
  testIdPrefix = "customer-autofill",
  hint,
}) {
  const [busy, setBusy] = useState(false);

  const handlePickerSelect = async (item) => {
    // item.metadata bevat de canonical autofill-payload uit
    // admin_entity_search.py::CustomerAdapter.search().
    const raw = { id: item?.id, ...(item?.metadata || {}) };
    let normalized = normalizeCustomer(raw);
    // Optioneel: haal /autofill op voor edge-cases (preferred_language,
    // alt_addresses[], etc.). Metadata bevat deze velden al standaard,
    // maar sommige oudere records missen ze — fetch garandeert volledigheid.
    if (fetchFull && normalized?.customer_id) {
      try {
        setBusy(true);
        const full = await fetchCustomerAutofill(normalized.customer_id);
        if (full) normalized = { ...normalized, ...full };
      } catch (_) {
        // silent — we hebben al de metadata payload
      } finally {
        setBusy(false);
      }
    }
    onSelect?.(normalized);
  };

  return (
    <div className="mb-3" data-testid={`${testIdPrefix}-container`}>
      {label && (
        <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-600">
          {label}
        </Label>
      )}
      <EnterpriseEntityPicker
        entityType="customer"
        mode="inline"
        testIdPrefix={testIdPrefix}
        autoFocus={autoFocus}
        showRecent={showRecent}
        placeholder={placeholder}
        onSelect={handlePickerSelect}
      />
      {hint && (
        <div className="text-[10px] text-gray-500 mt-1">{hint}</div>
      )}
      {busy && (
        <div className="text-[10px] text-blue-600 mt-1" data-testid={`${testIdPrefix}-loading`}>
          Klantgegevens laden…
        </div>
      )}
    </div>
  );
}
